import { useSyncExternalStore } from 'react';
import type { ProjectDraft } from './types';

/**
 * The drafts `draft_project` returned in a chat, by version. A draft is never
 * saved anywhere: it lives in the chat's tool results, and Create posts the
 * latest one. The draft canvas is keyed by the chat's thread id.
 */
export interface ProjectDraftEntry {
  draft: ProjectDraft;
  version: number;
}

const drafts = new Map<string, ProjectDraftEntry[]>();
// The project a chat's draft became, so the canvas and its cards say so and never create twice.
const created = new Map<string, { id: string; name: string; tint: string }>();
const listeners = new Set<() => void>();
const snapshots = new Map<string, ProjectDraftEntry[] | undefined>();

export function isProjectDraftOutput(
  value: unknown,
): value is { status: 'ok'; draft: ProjectDraft; version: number } {
  if (!value || typeof value !== 'object') return false;
  const output = value as { status?: unknown; draft?: unknown; version?: unknown };
  return (
    output.status === 'ok' &&
    typeof output.version === 'number' &&
    !!output.draft &&
    typeof output.draft === 'object' &&
    typeof (output.draft as { name?: unknown }).name === 'string'
  );
}

export function rememberProjectDraft(threadId: string, output: unknown) {
  if (!isProjectDraftOutput(output)) return;
  const list = drafts.get(threadId) ?? [];
  if (list.some((entry) => entry.version === output.version)) return;
  const next = [...list, { draft: output.draft, version: output.version }].sort(
    (a, b) => a.version - b.version,
  );
  drafts.set(threadId, next);
  snapshots.set(threadId, next);
  listeners.forEach((listener) => listener());
}

/** Every draft of the chat's project, oldest first; the canvas shows the last. */
export function useProjectDrafts(threadId: string) {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => snapshots.get(threadId),
  );
}

export function markProjectCreated(threadId: string, project: { id: string; name: string; tint: string }) {
  created.set(threadId, project);
  snapshots.set(threadId, [...(drafts.get(threadId) ?? [])]);
  listeners.forEach((listener) => listener());
}

/** The project this chat's draft became, if it was created here. */
export function useCreatedProject(threadId: string) {
  useProjectDrafts(threadId);
  return created.get(threadId) ?? null;
}
